'use client';

import { useMemo, useState } from 'react';
import { ChevronDown, Heart } from 'lucide-react';
import { PublicLayout } from '@/components/Layout/PublicLayout';
import { Loader } from '@/components/ui/loader';
import { FlexibleSupportCard } from '@/modules/subscriptions/components/FlexibleSupportCard';
import { PublicPlanCard } from '@/modules/subscriptions/components/PublicPlanCard';
import { SubscribeModal } from '@/modules/subscriptions/components/SubscribeModal';
import { SubscriptionPublicHero } from '@/modules/subscriptions/components/SubscriptionPublicHero';
import { SubscriptionStoreTheme } from '@/modules/subscriptions/components/SubscriptionStoreTheme';
import {
  useCreateOrgSubscriptionMutation,
  usePublicOrgSubscriptionsQuery,
  type PublicOrgSubscriptionsData,
} from '@/hooks/api/useSubscriptions';
import type { SubscriptionPlan } from '@/modules/subscriptions/interfaces/subscriptions';
import {
  getLowestPlanPrice,
  normalizePublicPlans,
} from '@/modules/subscriptions/utils/public-subscriptions';
import { toast } from 'sonner';

interface PublicOrgSubscriptionsProps {
  slug: string;
  initialData?: PublicOrgSubscriptionsData;
}

function pickFeaturedPlanIndex(
  plans: SubscriptionPlan[],
  featuredPlanId?: string | null,
): number {
  if (featuredPlanId) {
    const index = plans.findIndex((p) => p.id === featuredPlanId);
    if (index >= 0) return index;
  }
  if (plans.length <= 1) return 0;
  if (plans.length === 2) return 1;
  const withFeatures = plans.map((p, i) => ({
    i,
    score: (p.features?.length ?? 0) * 10 + (p.trial_days > 0 ? 5 : 0),
  }));
  withFeatures.sort((a, b) => b.score - a.score || plans[b.i].price - plans[a.i].price);
  return withFeatures[0]?.i ?? Math.floor(plans.length / 2);
}

export function PublicOrgSubscriptions({ slug, initialData }: PublicOrgSubscriptionsProps) {
  const { data, isLoading } = usePublicOrgSubscriptionsQuery(slug, initialData);
  const createMutation = useCreateOrgSubscriptionMutation(slug);

  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [flexibleAmount, setFlexibleAmount] = useState<number | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const payload = data ?? initialData;
  const organization = payload?.organization;
  const store = payload?.store;
  const plans = useMemo(
    () => normalizePublicPlans(payload?.plans ?? []),
    [payload?.plans],
  );

  const showFixedPlans = store?.fixed_plans_enabled !== false && plans.length > 0;
  const showFlexible = store?.flexible_enabled !== false;
  const minAmount = store?.min_amount ?? 1000;
  const defaultCurrency = store?.currency ?? plans[0]?.currency ?? 'CLP';
  const faqItems = store?.faq ?? [];
  const howItWorks = store?.how_it_works ?? [];

  const featuredIndex = useMemo(
    () => pickFeaturedPlanIndex(plans, store?.featured_plan_id),
    [plans, store?.featured_plan_id],
  );
  const lowestPrice = useMemo(() => getLowestPlanPrice(plans), [plans]);

  const heroTitle =
    store?.hero_title ?? (organization ? `Apoya a ${organization.name}` : 'Apoya a la organización');
  const heroSubtitle =
    store?.hero_subtitle ??
    'Únete como miembro y apoya el crecimiento del equipo con un aporte recurrente.';
  const ctaLabel = store?.cta_label ?? (plans.length > 0 ? 'Ver planes' : 'Apoyar ahora');

  const openPlanModal = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setFlexibleAmount(undefined);
    setModalOpen(true);
  };

  const openFlexibleModal = (amount: number) => {
    setSelectedPlan(null);
    setFlexibleAmount(amount);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedPlan(null);
    setFlexibleAmount(undefined);
  };

  const handleConfirmSubscribe = async (email: string) => {
    if (!selectedPlan && (!flexibleAmount || flexibleAmount < minAmount)) {
      toast.error(`Selecciona un monto de al menos $${minAmount.toLocaleString('es-CL')}`);
      return;
    }

    try {
      const response = await createMutation.mutateAsync({
        email,
        ...(selectedPlan ? { plan_id: selectedPlan.id } : { amount: flexibleAmount }),
      });

      const redirectUrl = response.init_point;
      if (!redirectUrl) {
        toast.error('No se recibió el enlace de Mercado Pago. Intenta nuevamente.');
        return;
      }

      window.location.href = redirectUrl;
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Error al procesar la suscripción');
    }
  };

  const copyPageLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  if (isLoading && !payload) {
    return (
      <PublicLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader text="Cargando suscripciones..." />
        </div>
      </PublicLayout>
    );
  }

  if (!organization) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-7xl px-4 py-24 text-center">
          <Heart className="mx-auto h-12 w-12 text-muted-foreground/30" />
          <h1 className="mt-4 font-[family-name:var(--font-outfit)] text-2xl font-bold">
            Organización no encontrada
          </h1>
          <p className="mt-2 text-muted-foreground">
            Esta página no existe o la organización no tiene suscripciones activas.
          </p>
        </div>
      </PublicLayout>
    );
  }

  if (store && !store.store_enabled) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-7xl px-4 py-24 text-center">
          <Heart className="mx-auto h-12 w-12 text-muted-foreground/30" />
          <h1 className="mt-4 font-[family-name:var(--font-outfit)] text-2xl font-bold">
            Landing no disponible
          </h1>
          <p className="mt-2 text-muted-foreground">
            {organization.name} no está aceptando suscripciones en este momento.
          </p>
        </div>
      </PublicLayout>
    );
  }

  if (!showFixedPlans && !showFlexible) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-7xl px-4 py-24 text-center">
          <Heart className="mx-auto h-12 w-12 text-muted-foreground/30" />
          <h1 className="mt-4 font-[family-name:var(--font-outfit)] text-2xl font-bold">
            Sin opciones de suscripción
          </h1>
          <p className="mt-2 text-muted-foreground">
            La organización aún no ha configurado planes ni aportes flexibles.
          </p>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SubscriptionStoreTheme organization={organization}>
        <SubscriptionPublicHero
          organization={organization}
          slug={slug}
          heroTitle={heroTitle}
          heroSubtitle={heroSubtitle}
          ctaLabel={ctaLabel}
          showFixedPlans={showFixedPlans}
          lowestPrice={lowestPrice}
          defaultCurrency={defaultCurrency}
          onCopyLink={() => void copyPageLink()}
        />

        {howItWorks.length > 0 && (
          <section className="border-b border-white/5 bg-card/20">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-14">
              <div className="grid gap-8 sm:grid-cols-3">
                {howItWorks.map(({ step, title, description }, i) => (
                  <div
                    key={`${step}-${title}`}
                    className="relative animate-in fade-in slide-in-from-bottom-3 duration-500 fill-mode-both"
                    style={{ animationDelay: `${i * 100}ms` }}
                  >
                    <span
                      className="font-[family-name:var(--font-outfit)] text-5xl font-bold opacity-20"
                      style={{ color: 'var(--subs-accent)' }}
                    >
                      {step}
                    </span>
                    <h3 className="mt-2 font-semibold text-lg">{title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                      {description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section id="planes" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24 scroll-mt-20">
          <div className="mb-12 max-w-2xl mx-auto text-center lg:mx-0 lg:text-left">
            <p className="text-xs uppercase tracking-[0.25em] text-[var(--subs-accent)] mb-3">
              {showFixedPlans ? 'Planes disponibles' : 'Apoyo flexible'}
            </p>
            <h2 className="font-[family-name:var(--font-outfit)] text-3xl font-bold tracking-tight sm:text-4xl">
              {showFixedPlans ? 'Elige cómo quieres apoyar' : 'Define tu aporte mensual'}
            </h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              Pago seguro con Mercado Pago.
              {showFlexible && showFixedPlans
                ? ' Puedes elegir un plan fijo o definir tu propio monto mensual.'
                : showFlexible
                  ? ` Define tu aporte mensual desde $${minAmount.toLocaleString('es-CL')}.`
                  : ' Elige el plan que mejor se adapte a ti.'}
            </p>
          </div>

          {showFixedPlans && (
            <div
              className={`
                mb-14 grid gap-6 lg:gap-8 items-stretch
                ${plans.length === 1 ? 'max-w-md mx-auto' : ''}
                ${plans.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : ''}
                ${plans.length >= 3 ? 'md:grid-cols-2 xl:grid-cols-3' : ''}
              `}
            >
              {plans.map((plan, index) => (
                <PublicPlanCard
                  key={plan.id}
                  plan={plan}
                  index={index}
                  featured={index === featuredIndex && plans.length > 1}
                  onSelect={openPlanModal}
                  disabled={createMutation.isPending}
                />
              ))}
            </div>
          )}

          {showFlexible && (
            <div className="max-w-4xl mx-auto">
              {showFixedPlans && (
                <div className="mb-8 text-center">
                  <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">
                    O personaliza tu aporte
                  </p>
                  <h3 className="font-[family-name:var(--font-outfit)] text-2xl font-bold">
                    Monto libre mensual
                  </h3>
                </div>
              )}
              <FlexibleSupportCard
                organizationName={organization.name}
                onSubscribe={() => {}}
                onOpenModal={openFlexibleModal}
                isLoading={createMutation.isPending}
                currency={defaultCurrency}
                minAmount={minAmount}
                suggestedAmounts={store?.suggested_amounts}
                defaultAmount={store?.default_amount ?? 3000}
                hasPublishedPlans={showFixedPlans}
                compact={showFixedPlans}
              />
            </div>
          )}
        </section>

        {faqItems.length > 0 && (
          <section className="border-t border-white/5 bg-card/15">
            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
              <div className="text-center mb-10">
                <h2 className="font-[family-name:var(--font-outfit)] text-2xl font-bold sm:text-3xl">
                  Preguntas frecuentes
                </h2>
              </div>
              <div className="space-y-2">
                {faqItems.map((item, i) => (
                  <div
                    key={item.q}
                    className="rounded-2xl border border-white/8 overflow-hidden transition-colors hover:border-[color-mix(in_srgb,var(--subs-accent)_20%,transparent)]"
                  >
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      aria-expanded={openFaq === i}
                    >
                      <span className="font-medium text-sm sm:text-base">{item.q}</span>
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                      />
                    </button>
                    <div
                      className={`grid transition-all duration-300 ease-out ${openFaq === i ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                    >
                      <div className="overflow-hidden">
                        <p className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed">
                          {item.a}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="border-t border-white/5">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 text-center">
            <p className="text-sm text-muted-foreground">
              Pagos procesados de forma segura por{' '}
              <span className="text-foreground/80 font-medium">Mercado Pago</span>
            </p>
            <p className="text-xs text-muted-foreground/60 mt-2">
              {organization.name} · Powered by Core Utils
            </p>
          </div>
        </section>

        <SubscribeModal
          plan={selectedPlan}
          flexibleAmount={flexibleAmount}
          open={modalOpen}
          onClose={closeModal}
          onConfirm={handleConfirmSubscribe}
          isLoading={createMutation.isPending}
          currency={selectedPlan?.currency ?? defaultCurrency}
        />
      </SubscriptionStoreTheme>
    </PublicLayout>
  );
}
